"use client";

import React, { useState } from "react";
import { Loader as Loader2, CircleCheck as CheckCircle2, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
interface FormValues {
  name: string;
  email: string;
  topic: string;
  message: string;
}
const topics = [{
  id: "general",
  label: "Consultas Generales"
}, {
  id: "ministries",
  label: "Ministerios y Departamentos"
}, {
  id: "church-directory",
  label: "Directorio y Localizador de Iglesias"
}, {
  id: "credentials",
  label: "Credenciales Ministeriales"
}, {
  id: "resources",
  label: "Publicaciones y Recursos"
}, {
  id: "giving",
  label: "Ofrendas y Misiones Mundiales"
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!captchaVerified) {
      setCaptchaError(true);
      return;
    }
    setCaptchaError(false);
    handleSubmit();
  };
  if (isSubmitted && !submitError) {
    return <div className="bg-card border border-border rounded-xl p-8 text-center space-y-4 shadow-sm">
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="text-xl font-semibold text-foreground font-default">
          Gracias por comunicarse con nosotros
        </h3>
        <p className="text-muted-foreground text-sm max-w-md mx-auto">
          {successMessage || "Su mensaje ha sido recibido por la Oficina Nacional de las Asambleas de Dios. Un representante se pondrá en contacto con usted en breve."}
        </p>
        <div className="pt-2">
          <Button type="button" variant="outline" className="rounded-lg" onClick={() => {
          setCaptchaVerified(false);
          resetForm();
        }}>
            Enviar Otro Mensaje
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={onSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Por favor, ingrese su nombre",
      minLength: {
        value: 2,
        message: "El nombre debe tener al menos 2 caracteres"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input placeholder="Nombre" className="bg-muted/30 hover:bg-muted/50 focus:bg-background border-border h-12 px-4 rounded-md text-foreground placeholder:text-muted-foreground text-base transition-colors" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Por favor, ingrese su correo electrónico",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Por favor, ingrese un correo electrónico válido"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input type="email" placeholder="Correo Electrónico" className="bg-muted/30 hover:bg-muted/50 focus:bg-background border-border h-12 px-4 rounded-md text-foreground placeholder:text-muted-foreground text-base transition-colors" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="topic" rules={{
      required: "Por favor, seleccione un tema"
    }} render={({
      field
    }) => <FormItem>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="w-full bg-muted/30 hover:bg-muted/50 focus:bg-background border-border h-12 px-4 rounded-md text-foreground text-base font-normal">
                  <SelectValue placeholder="-- Seleccione un Tema --" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-popover border-border">
                {topics.map((t, idx) => <SelectItem key={t.id} value={t.id} data-index={idx}>
                    {t.label}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Por favor, escriba su mensaje",
      minLength: {
        value: 10,
        message: "El mensaje debe tener al menos 10 caracteres"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <Textarea placeholder="Mensaje" rows={5} className="bg-muted/30 hover:bg-muted/50 focus:bg-background border-border p-4 rounded-md text-foreground placeholder:text-muted-foreground text-base min-h-[140px] resize-y transition-colors" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <div className="pt-2">
        <div className="inline-flex items-center justify-between border border-border bg-card rounded-md p-3 px-4 min-w-[280px] sm:min-w-[300px] shadow-2xs">
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={captchaVerified} onChange={e => {
            setCaptchaVerified(e.target.checked);
            if (e.target.checked) setCaptchaError(false);
          }} className="size-5 rounded border-border text-primary focus:ring-ring" />
            <span className="text-sm font-medium text-foreground">
              No soy un robot
            </span>
          </label>
          <div className="flex flex-col items-center pl-4 text-muted-foreground">
            <ShieldCheck className="size-6 text-muted-foreground/80" />
            <span className="text-[10px] tracking-tight uppercase text-muted-foreground/70">
              Control de Seguridad
            </span>
          </div>
        </div>
        {captchaError && <p className="text-destructive text-sm mt-1.5 font-medium">
            Por favor, verifique que no es un robot.
          </p>}
      </div>

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive rounded-md text-sm">
          {submitError}
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="bg-foreground text-background hover:bg-foreground/90 font-semibold uppercase tracking-wider px-8 py-2.5 h-11 rounded-lg text-sm transition-all shadow-xs">
          {isSubmitting ? <>
              <Loader2 className="animate-spin size-4 mr-2" />
              Enviando...
            </> : "ENVIAR"}
        </Button>
      </div>
    </Form>;
}
export default function Contact() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="contactofficeform" data-nav="light" className="bg-background text-foreground py-12 md:py-20 lg:py-24 border-b border-border transition-colors">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start">
          {}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-5 flex justify-center lg:justify-start">
            <div className="relative w-full max-w-md lg:max-w-none overflow-hidden rounded-2xl shadow-md border border-border/40 aspect-[4/5] bg-muted/20">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1768199/img/office_contact_person.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1768199/img/office_contact_person.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1768199/img/office_contact_person.png-wvc-sizes" alt="Una persona sosteniendo un teléfono y sonriendo en una mesa de café" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
            </div>
          </motion.div>

          {}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-7 flex flex-col justify-center">
            <div className="space-y-6">
              <div className="space-y-2">
                <p className="text-base sm:text-lg text-foreground font-default leading-relaxed">
                  Utilice este formulario para comunicarse con la Oficina Nacional de las Asambleas de Dios.
                  La información de contacto de las iglesias AD está disponible visitando la página del{" "}
                  <Link to="/MinistriesPage" className="text-destructive font-medium underline underline-offset-4 hover:opacity-80 transition-opacity">
                    Directorio de Iglesias
                  </Link>
                  .
                </p>
              </div>

              <FormProvider formId="contact-office-national" sectionName="contact-office-form" defaultValues={{
              name: "",
              email: "",
              topic: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              topic: {
                required: true
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="ENVIAR" successMessage="Su mensaje ha sido recibido por la Oficina Nacional de las Asambleas de Dios." className="w-full pt-2">
                <ContactFormInner />
              </FormProvider>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}